import React from 'react';
import { PageHeader, Badge, Stat, type ToneType } from './shared';
import { Icon } from './ui/icons';
import apiClient from '../lib/api-client';

// Utility function for conditional classes
function cx(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// Types
interface Control {
  id: string;
  name: string;
  status: 'draft' | 'published' | 'changed' | 'archived';
  pass: number;
  fail: number;
  pending: number;
  tags?: string[];
}

interface Framework {
  id: string;
  name: string;
  shortName: string;
  color: string;
  requirements: Array<{
    id: string;
    name: string;
    controls: string[];
  }>;
}

interface ConformanceRule {
  id: string;
  name: string;
  kind: string;
  condition?: {
    operands?: Array<{ tag?: string }>;
  };
  satisfies: Array<{
    framework: string;
    requirement: string;
  }>;
}

interface Tag {
  id: string;
  kind: 'behavior' | 'framework';
}

interface RiskAcceptance {
  id: string;
  expiresOn: string;
  repo: string;
  control: string;
}

interface RecommendationsData {
  frameworks?: {
    frameworks: Framework[];
  };
  controls?: {
    controls: Control[];
  };
  conformance?: {
    rules: ConformanceRule[];
  };
  tags?: {
    tags: Tag[];
  };
  risks?: {
    acceptances: RiskAcceptance[];
  };
}

interface RecommendationTarget {
  framework: string;
  req: string;
}

interface Recommendation {
  id: string;
  priority: 'high' | 'medium' | 'low';
  kind: 'publish-draft' | 'map-tag-based' | 'create-rule' | 'renew-acceptance' | 'investigate';
  title: string;
  sub?: string;
  why: string;
  impactLabel: string;
  effort: string;
  icon: string;
  tone: string;
  ctaLabel: string;
  ctaRoute: string;
  targets?: RecommendationTarget[];
}

interface RecommendationsEngineOptions {
  frameworkId?: string;
  limit?: number;
}

interface RecommendationsProps {
  data: RecommendationsData;
  navigate: (path: string) => void;
}

interface RecommendationsPanelProps {
  data: RecommendationsData;
  navigate: (path: string) => void;
  opts?: RecommendationsEngineOptions;
  title?: string;
  subtitle?: string;
  limit?: number;
  viewAll?: boolean;
  compact?: boolean;
}

interface RecommendationCardProps {
  rec: Recommendation;
  data: RecommendationsData;
  navigate: (path: string) => void;
  compact?: boolean;
}

// Recommendation computation engine
export function computeRecommendations(data: RecommendationsData, opts: RecommendationsEngineOptions = {}): Recommendation[] {
  const recs: Recommendation[] = [];
  if (!data?.frameworks || !data?.controls || !data?.tags) return recs;

  const frameworks = data.frameworks.frameworks;
  const controls = data.controls.controls;
  const conformanceRules = data.conformance?.rules || [];
  const tagDefs = data.tags.tags;
  const acceptances = data.risks?.acceptances || [];

  const passingRate = (c: Control) => {
    const total = c.pass + c.fail + c.pending;
    return total ? c.pass / total : 0;
  };
  
  const isHealthy = (c: Control) => c.status === 'published' && passingRate(c) >= 0.85;

  // Helper: does this framework include the scope we're filtering by?
  const matchesScope = (frameworkId: string) => {
    if (!opts.frameworkId) return true;
    return opts.frameworkId === frameworkId;
  };

  // ────── Category A: Publish draft controls that already map to requirements ─
  controls
    .filter((c) => c.status === 'draft')
    .forEach((c) => {
      const matchingReqs: Array<{ framework: Framework; req: Framework['requirements'][0] }> = [];
      frameworks.forEach((f) => {
        f.requirements.forEach((r) => {
          if (r.controls.includes(c.id)) matchingReqs.push({ framework: f, req: r });
        });
      });
      if (!matchingReqs.length) return;
      if (!matchingReqs.some(({ framework }) => matchesScope(framework.id))) return;
      
      recs.push({
        id: `publish-${c.id}`,
        priority: matchingReqs.length >= 2 ? 'high' : 'medium',
        kind: 'publish-draft',
        title: `Publish draft control #${c.id}`,
        sub: c.name,
        why: `${matchingReqs.length} framework requirement${matchingReqs.length > 1 ? 's' : ''} are waiting on this — ${matchingReqs.map((m) => `${m.framework.shortName} ${m.req.id}`).join(', ')}.`,
        impactLabel: `+${matchingReqs.length} requirement${matchingReqs.length > 1 ? 's' : ''} satisfied`,
        effort: '1-click',
        icon: 'shield',
        tone: 'draft',
        ctaLabel: 'Open & publish',
        ctaRoute: `controls/${c.id}`,
        targets: matchingReqs.map((m) => ({
          framework: m.framework.id,
          req: m.req.id,
        })),
      });
    });

  // ────── Category B: Apply existing healthy control to a partial requirement ─
  frameworks.forEach((f) => {
    if (!matchesScope(f.id)) return;
    f.requirements.forEach((r) => {
      const mapped = r.controls.map((id) => controls.find((c) => c.id === id)).filter(Boolean);
      const ok = mapped.length && mapped.every(isHealthy);
      if (ok) return;

      const reqTags = inferRequirementTags(r, data);
      if (!reqTags.length) return;
      const candidates = controls.filter(
        (c) =>
          !r.controls.includes(c.id) &&
          (c.tags || []).some((t) => reqTags.includes(t)) &&
          c.status !== 'archived'
      );
      candidates.forEach((c) => {
        recs.push({
          id: `map-${c.id}-to-${f.id}-${r.id}`,
          priority: 'medium',
          kind: 'map-tag-based',
          title: `Tag #${c.id} with '${reqTags[0]}'`,
          sub: c.name,
          why: `${f.shortName} ${r.id} is annotated '${reqTags[0]}'. Tag this control so the tag-based rule picks it up automatically.`,
          impactLabel: `+1 requirement satisfied across ${f.shortName}`,
          effort: '1-click',
          icon: 'tag',
          tone: 'info',
          ctaLabel: 'Tag this control',
          ctaRoute: `controls/${c.id}`,
          targets: [{ framework: f.id, req: r.id }],
        });
      });
    });
  });

  // ────── Category C: Behavior tag has no rule consuming it ──────────────────
  tagDefs
    .filter((t) => t.kind === 'behavior')
    .forEach((t) => {
      const usedByRule = conformanceRules.some((rule) =>
        (rule.condition?.operands || []).some((op) => op.tag === t.id)
      );
      if (usedByRule) return;
      
      const taggedControls = controls.filter((c) => (c.tags || []).includes(t.id));
      if (!taggedControls.length) return;
      
      recs.push({
        id: `rule-for-${t.id}`,
        priority: 'medium',
        kind: 'create-rule',
        title: `Author a tag-based rule for '${t.id}'`,
        sub: `${taggedControls.length} control${taggedControls.length > 1 ? 's' : ''} already wear this tag`,
        why: `No conformance rule consumes the behavior tag '${t.id}' yet. Authoring one would auto-cover every requirement annotated with it.`,
        impactLabel: 'Open coverage path',
        effort: '10 min',
        icon: 'git-branch',
        tone: 'draft',
        ctaLabel: 'New conformance rule',
        ctaRoute: 'conformance',
        targets: [],
      });
    });

  // ────── Category D: Risk acceptances expiring in < 30 days ─────────────────
  const now = Date.now();
  acceptances
    .filter((a) => {
      const d = (new Date(a.expiresOn).getTime() - now) / 86400000;
      return d <= 30 && d >= 0;
    })
    .forEach((a) => {
      recs.push({
        id: `accept-renew-${a.id}`,
        priority: 'high',
        kind: 'renew-acceptance',
        title: `Renew or address ${a.id}`,
        sub: `${a.repo} · #${a.control}`,
        why: `Risk acceptance expires on ${a.expiresOn}. Renew, replace with a fix, or it will start failing controls again.`,
        impactLabel: 'Avoid regression',
        effort: '30 min',
        icon: 'alert-tri',
        tone: 'warn',
        ctaLabel: 'Open acceptance',
        ctaRoute: 'risk-acceptances',
        targets: [],
      });
    });

  // ────── Category E: Controls with very low pass rate (investigate) ─────────
  controls
    .filter(
      (c) => c.status === 'published' && c.pass + c.fail + c.pending > 0 && passingRate(c) < 0.7
    )
    .forEach((c) => {
      recs.push({
        id: `investigate-${c.id}`,
        priority: 'high',
        kind: 'investigate',
        title: `Investigate #${c.id} failures`,
        sub: c.name,
        why: `Pass rate is ${Math.round(passingRate(c) * 100)}% (target 85%+). Either the interpreter is mis-calibrated or the underlying problem is real.`,
        impactLabel: `Restore ${c.fail} failing run${c.fail > 1 ? 's' : ''}`,
        effort: '1h',
        icon: 'alert-tri',
        tone: 'crit',
        ctaLabel: 'Open control',
        ctaRoute: `controls/${c.id}`,
        targets: [],
      });
    });

  // Stable sort: priority then kind
  const priorityWeight = { high: 0, medium: 1, low: 2 };
  recs.sort((a, b) => priorityWeight[a.priority] - priorityWeight[b.priority] || a.kind.localeCompare(b.kind));

  return opts.limit ? recs.slice(0, opts.limit) : recs;
}

// Infer behavior tags annotating a framework requirement.
function inferRequirementTags(req: Framework['requirements'][0], _data: RecommendationsData): string[] {
  const REQ_TAG_MAP: Record<string, string[]> = {
    'CC6.6': ['encryption-at-rest', 'encryption'],
    'CC6.1': ['access-control'],
    'CC7.1': ['audit-logging'],
    'CC7.2': ['audit-logging'],
    'CC8.1': ['vuln-management'],
    'A1.2': ['backup'],
    'A.5.15': ['access-control'],
    'A.8.10': ['data-retention'],
    'A.8.11': ['data-anonymization'],
    'A.8.24': ['encryption-at-rest', 'encryption'],
    'A.8.8': ['vuln-management'],
    'A.8.15': ['audit-logging'],
    'A.8.13': ['backup'],
    'Art. 32(1)(a)': ['encryption-at-rest', 'encryption'],
    'Art. 32(1)(b)': ['access-control'],
    'Art. 32(1)(d)': ['vuln-management'],
    'Art. 5(1)(c)': ['data-anonymization'],
    'Art. 5(1)(e)': ['data-retention'],
    'Art. 17': ['data-retention'],
    'Art. 7': ['consent'],
    'Req. 3': ['encryption-at-rest', 'encryption'],
    'Req. 6': ['vuln-management'],
    'Req. 7': ['access-control'],
    'Req. 8': ['access-control'],
    'Req. 10': ['audit-logging'],
  };
  return REQ_TAG_MAP[req.id] || [];
}

// Recommendation card component
export function RecommendationCard({ rec, data, navigate, compact }: RecommendationCardProps) {
  const priColor =
    rec.priority === 'high' ? '#F40B0B' : rec.priority === 'medium' ? '#CA9401' : '#6B7280';
    
  return (
    <div
      className={cx(
        'rounded-[10px] border bg-white flex flex-col gap-3',
        rec.priority === 'high' ? 'border-[#FDD3D3]' : 'border-[#F3E4C8]',
        compact ? 'p-4' : 'p-5'
      )}
    >
      <RecommendationHeader rec={rec} priColor={priColor} />
      <RecommendationDescription why={rec.why} />
      {rec.targets && rec.targets.length > 0 && (
        <RecommendationTargets targets={rec.targets} data={data} />
      )}
      <RecommendationFooter rec={rec} navigate={navigate} />
    </div>
  );
}

interface RecommendationHeaderProps {
  rec: Recommendation;
  priColor: string;
}

function RecommendationHeader({ rec, priColor }: RecommendationHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0">
        <div
          className="h-9 w-9 rounded-[8px] grid place-items-center shrink-0"
          style={{
            background: `${priColor}1a`,
            border: `1px solid ${priColor}33`,
          }}
        >
          <Icon name={rec.icon} size={16} stroke={priColor} />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-ink text-[14px]">{rec.title}</div>
          {rec.sub && (
            <div className="font-data text-[12px] text-ink/55 mt-0.5 truncate">{rec.sub}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge
          tone={rec.priority === 'high' ? 'crit' : rec.priority === 'medium' ? 'warn' : 'muted'}
          dot
        >
          {rec.priority}
        </Badge>
      </div>
    </div>
  );
}

interface RecommendationDescriptionProps {
  why: string;
}

function RecommendationDescription({ why }: RecommendationDescriptionProps) {
  return (
    <p className="font-display text-[13px] text-ink/75 leading-relaxed">{why}</p>
  );
}

interface RecommendationTargetsProps {
  targets: RecommendationTarget[];
  data: RecommendationsData;
}

function RecommendationTargets({ targets, data }: RecommendationTargetsProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {targets.slice(0, 4).map((t) => {
        const f = data.frameworks?.frameworks.find((x) => x.id === t.framework);
        return (
          <span
            key={`${t.framework}-${t.req}`}
            className="inline-flex items-center gap-1.5 h-6 px-1.5 rounded-[5px]"
            style={{ background: f ? `${f.color}1a` : '#F7EDDB' }}
          >
            <span
              className="font-data text-[10px] font-bold"
              style={{ color: f?.color || '#6B7280' }}
            >
              {f?.shortName}
            </span>
            <span className="font-data text-[11px] text-ink/70">{t.req}</span>
          </span>
        );
      })}
      {targets.length > 4 && (
        <span className="font-data text-[11px] text-ink/55">
          +{targets.length - 4} more
        </span>
      )}
    </div>
  );
}

interface RecommendationFooterProps {
  rec: Recommendation;
  navigate: (path: string) => void;
}

function RecommendationFooter({ rec, navigate }: RecommendationFooterProps) {
  return (
    <div className="flex items-center justify-between pt-3 border-t border-[#F7EDDB]">
      <div className="flex items-center gap-3 font-data text-[12px]">
        <span className="text-ink/55">Impact:</span>
        <span className="font-display font-bold text-[#159D4C] text-[12.5px]">
          {rec.impactLabel}
        </span>
        <span className="text-ink/30">·</span>
        <span className="text-ink/55">Effort:</span>
        <span className="font-display font-bold text-ink text-[12.5px]">{rec.effort}</span>
      </div>
      <button
        type="button"
        onClick={() => navigate(rec.ctaRoute)}
        className="h-8 px-3 rounded-[8px] bg-ink text-white font-display font-bold text-[12.5px] hover:opacity-90 inline-flex items-center gap-1.5 whitespace-nowrap"
      >
        {rec.ctaLabel} <Icon name="arrow-up-right" size={12} stroke="white" />
      </button>
    </div>
  );
}

// Reusable recommendations panel
export function RecommendationsPanel({
  data,
  navigate,
  opts = {},
  title = 'Recommended actions',
  subtitle,
  limit = 4,
  viewAll = true,
  compact = false,
}: RecommendationsPanelProps) {
  const recs = computeRecommendations(data, opts);
  const shown = recs.slice(0, limit);
  
  return (
    <div className="rounded-[10px] border border-[#F3E4C8] bg-white overflow-hidden">
      <RecommendationsPanelHeader 
        title={title}
        recsCount={recs.length}
        viewAll={viewAll}
        navigate={navigate}
      />
      {subtitle && (
        <div className="px-5 pt-3 font-display text-[13px] text-ink/65">{subtitle}</div>
      )}
      <div className={cx('p-4 grid gap-3', compact ? 'grid-cols-1' : 'grid-cols-2')}>
        {shown.length === 0 ? (
          <div className="font-data text-[13px] text-ink/45 text-center py-6 col-span-2">
            No outstanding recommendations 🎉
          </div>
        ) : (
          shown.map((rec) => (
            <RecommendationCard
              key={rec.id}
              rec={rec}
              data={data}
              navigate={navigate}
              compact={compact}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface RecommendationsPanelHeaderProps {
  title: string;
  recsCount: number;
  viewAll: boolean;
  navigate: (path: string) => void;
}

function RecommendationsPanelHeader({ title, recsCount, viewAll, navigate }: RecommendationsPanelHeaderProps) {
  return (
    <div className="px-5 py-3 border-b border-[#F7EDDB] bg-[#FCF8F1]/60 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon name="arrow-up-right" size={16} stroke="#FA500E" />
        <span className="font-display font-bold text-[14px] uppercase tracking-[0.04em] text-ink">
          {title}
        </span>
        <Badge tone="info">{recsCount}</Badge>
      </div>
      {viewAll && (
        <button
          type="button"
          onClick={() => navigate('recommendations')}
          className="font-display text-[13px] font-semibold text-[#FA500E] hover:underline inline-flex items-center gap-1"
        >
          View all <Icon name="arrow-up-right" size={12} stroke="currentColor" />
        </button>
      )}
    </div>
  );
}

// Priority filter component
interface PriorityFilterProps {
  filter: string;
  onFilterChange: (filter: string) => void;
  recommendations: Recommendation[];
}

function PriorityFilter({ filter, onFilterChange, recommendations }: PriorityFilterProps) {
  const byPriority = {
    high: recommendations.filter((r) => r.priority === 'high'),
    medium: recommendations.filter((r) => r.priority === 'medium'),
    low: recommendations.filter((r) => r.priority === 'low'),
  };

  return (
    <div className="flex items-center gap-2">
      <span className="font-data text-[12px] text-ink/55">Priority:</span>
      {(['all', 'high', 'medium', 'low'] as const).map((p) => (
        <button
          type="button"
          key={p}
          onClick={() => onFilterChange(p)}
          className={cx(
            'h-9 px-3 rounded-[8px] font-display text-[13px] inline-flex items-center gap-2',
            filter === p ? 'bg-ink text-white font-semibold' : 'text-ink/70 hover:bg-[#F7EDDB]'
          )}
        >
          {p}
          <span
            className={cx(
              'font-data text-[11px] rounded-full px-1.5 min-w-[20px] text-center',
              filter === p ? 'bg-white/15' : 'bg-[#F7EDDB] text-ink/55'
            )}
          >
            {p === 'all' ? recommendations.length : byPriority[p as keyof typeof byPriority]?.length || 0}
          </span>
        </button>
      ))}
    </div>
  );
}

// Helper functions for transforming API data
function getIconForKind(kind: string): string {
  const iconMap: Record<string, string> = {
    'publish-draft': 'shield',
    'map-tag-based': 'tag',
    'create-rule': 'git-branch',
    'renew-acceptance': 'alert-triangle',
    'investigate': 'alert-triangle',
    'patch-vulnerability': 'shield',
    'resolve-incident': 'alert-circle',
  };
  return iconMap[kind] || 'alert-circle';
}

function getToneForPriority(priority: string): string {
  const toneMap: Record<string, string> = {
    'critical': 'crit',
    'high': 'crit',
    'medium': 'warn',
    'low': 'info',
  };
  return toneMap[priority] || 'info';
}

function getCtaForKind(kind: string): string {
  const ctaMap: Record<string, string> = {
    'publish-draft': 'Open & publish',
    'map-tag-based': 'Tag this control',
    'create-rule': 'Create rule',
    'renew-acceptance': 'Open acceptance',
    'investigate': 'Investigate',
    'patch-vulnerability': 'Apply patch',
    'resolve-incident': 'Resolve incident',
  };
  return ctaMap[kind] || 'Take action';
}

function getRouteForKind(kind: string, id: string): string {
  const routeMap: Record<string, string> = {
    'publish-draft': `controls/${id}`,
    'map-tag-based': `controls/${id}`,
    'create-rule': 'conformance',
    'renew-acceptance': 'risk-acceptances',
    'investigate': `controls/${id}`,
    'patch-vulnerability': `security-events/${id}`,
    'resolve-incident': `security-events/${id}`,
  };
  return routeMap[kind] || `remediations/${id}`;
}

function parseTargets(relatedFrameworks?: string): RecommendationTarget[] {
  if (!relatedFrameworks) return [];
  try {
    const frameworks = JSON.parse(relatedFrameworks);
    return frameworks.map((fw: any) => ({
      framework: fw.framework || fw,
      req: fw.requirement || fw.req || '',
    }));
  } catch {
    return [];
  }
}

function getMockRemediations(): Recommendation[] {
  return [
    {
      id: 'rem-001',
      priority: 'high',
      kind: 'publish-draft',
      title: 'Publish draft control #CTRL-001',
      sub: 'Multi-factor Authentication Enforcement',
      why: '2 framework requirements are waiting on this — SOC2 CC6.1, ISO27001 A.9.4.2.',
      impactLabel: '+2 requirements satisfied',
      effort: '1-click',
      icon: 'shield',
      tone: 'draft',
      ctaLabel: 'Open & publish',
      ctaRoute: 'controls/CTRL-001',
      targets: [
        { framework: 'soc2', req: 'CC6.1' },
        { framework: 'iso27001', req: 'A.9.4.2' }
      ]
    },
    {
      id: 'rem-002',
      priority: 'high',
      kind: 'investigate',
      title: 'Investigate #CTRL-023 failures',
      sub: 'Database Encryption at Rest',
      why: 'Pass rate is 42% (target 85%+). Either the interpreter is mis-calibrated or the underlying problem is real.',
      impactLabel: 'Restore 15 failing runs',
      effort: '1h',
      icon: 'alert-triangle',
      tone: 'crit',
      ctaLabel: 'Open control',
      ctaRoute: 'controls/CTRL-023',
      targets: []
    },
    {
      id: 'rem-003',
      priority: 'medium',
      kind: 'map-tag-based',
      title: 'Tag #CTRL-012 with "access-control"',
      sub: 'Role-Based Access Control Implementation',
      why: 'SOC2 CC6.1 is annotated "access-control". Tag this control so the tag-based rule picks it up automatically.',
      impactLabel: '+1 requirement satisfied across SOC2',
      effort: '1-click',
      icon: 'tag',
      tone: 'info',
      ctaLabel: 'Tag this control',
      ctaRoute: 'controls/CTRL-012',
      targets: [
        { framework: 'soc2', req: 'CC6.1' }
      ]
    }
  ];
}

export default function Remediations({ data, navigate }: RecommendationsProps) {
  const [filter, setFilter] = React.useState('all');
  const [recs, setRecs] = React.useState<Recommendation[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch remediations from API
  React.useEffect(() => {
    const fetchRemediations = async () => {
      try {
        setLoading(true);
        const result = await apiClient.getRemediations();
        if (result.success && result.data) {
          // Transform API data to match component interface
          const transformedData = result.data.map((item: any) => ({
            id: item.id,
            priority: item.priority,
            kind: item.kind,
            title: item.title,
            sub: item.description,
            why: item.impact,
            impactLabel: item.estimatedImpact ? `Impact score: ${item.estimatedImpact}` : item.impact,
            effort: item.effort,
            icon: getIconForKind(item.kind),
            tone: getToneForPriority(item.priority),
            ctaLabel: getCtaForKind(item.kind),
            ctaRoute: getRouteForKind(item.kind, item.id),
            targets: parseTargets(item.relatedFrameworks),
          }));
          setRecs(transformedData);
        } else {
          // Fallback to mock data if API fails
          setRecs(getMockRemediations());
        }
      } catch (err) {
        console.error('Error fetching remediations:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        // Fallback to mock data
        setRecs(getMockRemediations());
      } finally {
        setLoading(false);
      }
    };

    fetchRemediations();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-ink/60">Loading remediations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-red-600 mb-2">Error: {error}</p>
          <p className="text-ink/60">Showing mock data instead</p>
        </div>
      </div>
    );
  }
  const byPriority = {
    high: recs.filter((r) => r.priority === 'high'),
    medium: recs.filter((r) => r.priority === 'medium'),
    low: recs.filter((r) => r.priority === 'low'),
  };
  const list = filter === 'all' ? recs : recs.filter((r) => r.priority === filter);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Remediations"
        subtitle="Automated security and compliance remediation actions. Each recommendation shows impact and effort to help prioritize your workplan."
        right={
          <button
            type="button"
            onClick={() => navigate('dashboard')}
            className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40 shrink-0"
          >
            <Icon name="dashboard" size={14} stroke="#121111" /> Back to dashboard
          </button>
        }
      />

      <div className="grid grid-cols-4 gap-4">
        <Stat label="Open actions" value={recs.length} />
        <Stat
          label="High priority"
          value={byPriority.high.length}
          tone={byPriority.high.length ? 'crit' : 'ok'}
        />
        <Stat label="Medium priority" value={byPriority.medium.length} tone="warn" />
        <Stat
          label="1-click wins"
          value={recs.filter((r) => r.effort === '1-click').length}
          tone="info"
        />
      </div>

      <PriorityFilter 
        filter={filter}
        onFilterChange={setFilter}
        recommendations={recs}
      />

      <div className="grid grid-cols-2 gap-4">
        {list.map((rec) => (
          <RecommendationCard key={rec.id} rec={rec} data={data} navigate={navigate} />
        ))}
        {!list.length && (
          <div className="col-span-2 py-12 text-center font-display text-ink/45">
            No actions in this priority band.
          </div>
        )}
      </div>
    </div>
  );
}